import React, { useState } from 'react';
import Modal from 'react-modal';

function ImageView({ images, onImageUpload, onImageDelete, onImageCopy, onImageView, onImageUpdate }) {
  const [viewMode, setViewMode] = useState('grid');
  const [image, setImage] = useState(null);
  
  // Expiration State
  const [expirationMode, setExpirationMode] = useState('never'); // never, 10m, 1h, 1d, 1w, custom
  const [customExpiration, setCustomExpiration] = useState('');

  // Edit State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [editExpiresAt, setEditExpiresAt] = useState('');

  const handleUpload = (e) => {
    e.preventDefault();
    if (!image) return;
    
    let expiresAt = null;
    const now = new Date();
    
    switch (expirationMode) {
      case '10m':
        now.setMinutes(now.getMinutes() + 10);
        expiresAt = now.toISOString();
        break;
      case '1h':
        now.setHours(now.getHours() + 1);
        expiresAt = now.toISOString();
        break;
      case '1d':
        now.setDate(now.getDate() + 1);
        expiresAt = now.toISOString();
        break;
      case '1w':
        now.setDate(now.getDate() + 7);
        expiresAt = now.toISOString();
        break;
      case 'custom':
        if (customExpiration) {
          expiresAt = new Date(customExpiration).toISOString();
        }
        break;
      default:
        expiresAt = null;
    }

    onImageUpload(image, { is_public: true, expires_at: expiresAt });
    
    setImage(null);
    setExpirationMode('never');
    setCustomExpiration('');
    e.target.reset();
  };

  const openEditModal = (img) => {
    setEditingImage(img);
    setEditIsPublic(img.is_public);
    setEditExpiresAt(img.expires_at ? new Date(img.expires_at).toISOString().slice(0, 16) : '');
    setEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!editingImage) return;
    onImageUpdate(editingImage.id, {
      is_public: editIsPublic,
      expires_at: editExpiresAt || ''
    });
    setEditModalOpen(false);
  };

  return (
    <div>
      <div className="flex-between mb-4">
        <h2 className="mt-0 mb-0">Images</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            onClick={() => setViewMode('grid')} 
            className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Grid
          </button>
          <button 
            onClick={() => setViewMode('list')} 
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
          >
            List
          </button>
        </div>
      </div>
      <div className="card mb-4">
        <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input type="file" accept="image/*" onChange={e => setImage(e.target.files[0])} className="input" style={{ width: 'auto', flex: 1 }} />
            <button type="submit" className="btn btn-primary" disabled={!image}>Upload Image</button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Expiration Control */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontWeight: 600, fontSize: '0.9em' }}>Expiration</label>
              <select 
                value={expirationMode} 
                onChange={e => setExpirationMode(e.target.value)} 
                className="input"
              >
                <option value="never">Never</option>
                <option value="10m">10 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="1d">1 Day</option>
                <option value="1w">1 Week</option>
                <option value="custom">Custom Date/Time</option>
              </select>
              {expirationMode === 'custom' && (
                <input 
                  type="datetime-local" 
                  value={customExpiration} 
                  onChange={e => setCustomExpiration(e.target.value)} 
                  className="input"
                />
              )}
            </div>
          </div>
        </form>
      </div>
      <div className={viewMode === 'grid' ? 'view-grid' : 'view-list'}>
        {images.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>No images found.</div>}
        {images.map(img => {
          const token = localStorage.getItem('token');
          const thumbnailUrl = `${window.location.origin}/image/view/${img.id}/${encodeURIComponent(img.filename || img.name)}${!img.is_public ? `?token=${token}` : ''}`;
          
          let shareUrl;
          if (img.is_public) {
            shareUrl = `${window.location.origin}/image/view/${img.id}/${encodeURIComponent(img.filename || img.name)}`;
          } else {
            shareUrl = `${window.location.origin}/s/image/${img.id}/${encodeURIComponent(img.filename || img.name)}`;
          }
          
          return (
            <div key={img.id} className="view-item">
              <div className="view-item-icon" style={{ overflow: 'hidden' }}>
                <img 
                  src={thumbnailUrl} 
                  alt={img.filename || img.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerText = '🖼️'; }}
                />
              </div>
              <div className="view-item-details">
                <div className="view-item-name" title={img.filename || img.name}>{img.filename || img.name}</div>
                <div className="view-item-id">ID: {img.id}</div>
                <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)', marginTop: 4 }}>
                  {img.is_public ? '🌐 Public' : '🔒 Private'}
                  {img.expires_at && ` • Expires: ${new Date(img.expires_at).toLocaleDateString()}`}
                </div>
              </div>
              <div className="view-item-actions">
                <button onClick={() => onImageCopy(`<img src="${thumbnailUrl}" alt="${img.filename || img.name}" />`)} title="Copy HTML Tag" className="btn-icon">{'</>'}</button>
                <button onClick={() => onImageCopy(shareUrl)} title="Copy Share URL" className="btn-icon">📋</button>
                <button onClick={() => onImageView(img.id, img.filename || img.name)} title="View" className="btn-icon">👁️</button>
                {img.is_owner && (
                  <>
                    <button onClick={() => openEditModal(img)} title="Edit" className="btn-icon">✏️</button>
                    <button onClick={() => onImageDelete(img.id)} title="Delete" className="btn-icon" style={{ color: 'var(--danger)' }}>🗑️</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={editModalOpen}
        onRequestClose={() => setEditModalOpen(false)}
        contentLabel="Edit Image"
        style={{ content: { maxWidth: 500, margin: 'auto', borderRadius: 8, padding: 24, height: 'fit-content' } }}
      >
        <h2 style={{ marginTop: 0 }}>Edit Image</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={editIsPublic} 
                onChange={e => setEditIsPublic(e.target.checked)} 
              />
              Public
            </label>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Expires At</label>
            <input 
              type="datetime-local" 
              value={editExpiresAt} 
              onChange={e => setEditExpiresAt(e.target.value)} 
              className="input"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Leave empty for no expiration</small>
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button onClick={() => setEditModalOpen(false)} className="btn btn-secondary">Cancel</button>
            <button onClick={handleUpdate} className="btn btn-primary">Save Changes</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ImageView;

